/**
 * FamilyPanel — manage the family group of a volunteer.
 *
 * Shown inside the "Datos Personales" tab of VolunteerForm. Lets the
 * CRO operator:
 *  - See current family members (titulares + dependientes).
 *  - Link another volunteer (search by name/DNI/email).
 *  - Change a member's role/relationship.
 *  - Unlink a member.
 *
 * Backend endpoints used (see /app/backend/routes/family_routes.py):
 *  GET    /api/volunteers/{id}/family
 *  POST   /api/volunteers/{id}/family/link
 *  POST   /api/volunteers/{id}/family/unlink
 *  PUT    /api/volunteers/{id}/family/role
 *
 * Search picker reuses GET /api/volunteers?search=X&all=true.
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  UsersThree, Link as LinkIcon, X, MagnifyingGlass, ArrowsClockwise,
  Crown, Baby,
} from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { errText } from '../../utils/errors';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_OPTS = [
  { value: 'titular', es: 'Titular (responsable legal)', en: 'Holder (legal guardian)' },
  { value: 'dependiente', es: 'Dependiente (menor o representado)', en: 'Dependent (minor or represented)' },
];

export default function FamilyPanel({ volunteerId, currentVolunteerName, lang }) {
  const [family, setFamily] = useState({ family_id: null, members: [] });
  const [loading, setLoading] = useState(false);
  const [showLink, setShowLink] = useState(false);

  const load = useCallback(async () => {
    if (!volunteerId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/volunteers/${volunteerId}/family`,
        { withCredentials: true },
      );
      setFamily(data || { family_id: null, members: [] });
    } catch (e) {
      toast.error(errText(e, lang === 'es' ? 'Error al cargar familia' : 'Error loading family'));
    } finally {
      setLoading(false);
    }
  }, [volunteerId, lang]);

  useEffect(() => { load(); }, [load]);

  const handleUnlink = async (memberId) => {
    const msg = lang === 'es'
      ? '¿Desvincular este voluntario de la familia?'
      : 'Unlink this volunteer from the family?';
    if (!window.confirm(msg)) return;
    try {
      const { data } = await axios.post(
        `${API_URL}/api/volunteers/${volunteerId}/family/unlink`,
        { linked_volunteer_id: memberId },
        { withCredentials: true },
      );
      setFamily(data);
      toast.success(lang === 'es' ? 'Desvinculado' : 'Unlinked');
    } catch (e) {
      toast.error(errText(e, lang === 'es' ? 'Error al desvincular' : 'Unlink error'));
    }
  };

  const handleRoleChange = async (memberId, newRole, currentRelationship) => {
    try {
      const { data } = await axios.put(
        `${API_URL}/api/volunteers/${memberId}/family/role`,
        { role: newRole, relationship: currentRelationship || '' },
        { withCredentials: true },
      );
      setFamily(data);
      toast.success(lang === 'es' ? 'Rol actualizado' : 'Role updated');
    } catch (e) {
      toast.error(errText(e, lang === 'es' ? 'Error' : 'Error'));
    }
  };

  const handleRelationshipBlur = async (memberId, role, relationship) => {
    try {
      await axios.put(
        `${API_URL}/api/volunteers/${memberId}/family/role`,
        { role, relationship },
        { withCredentials: true },
      );
    } catch (e) {
      toast.error(errText(e, lang === 'es' ? 'Error' : 'Error'));
    }
  };

  const onLinked = (data) => {
    setFamily(data);
    setShowLink(false);
  };

  return (
    <div className="bg-white border border-stone-200 rounded-sm p-5 mt-5" data-testid="family-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <UsersThree size={18} className="text-[#1f3e80]" weight="duotone" />
          <h3 className="text-sm font-semibold text-stone-800">
            {lang === 'es' ? 'Familia / Vinculaciones' : 'Family / Links'}
          </h3>
          {family.members.length > 0 && (
            <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
              {family.members.length} {lang === 'es' ? 'miembros' : 'members'}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="text-[11px] text-stone-500 hover:text-stone-700 disabled:opacity-50 flex items-center gap-1"
            data-testid="family-reload"
          >
            <ArrowsClockwise size={12} /> {lang === 'es' ? 'Refrescar' : 'Refresh'}
          </button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowLink(true)}
            data-testid="family-link-btn"
            className="gap-1.5"
          >
            <LinkIcon size={14} /> {lang === 'es' ? 'Vincular voluntario' : 'Link volunteer'}
          </Button>
        </div>
      </div>

      <p className="text-xs text-stone-500 mb-4">
        {lang === 'es'
          ? 'Asocia voluntarios que forman parte del mismo nucleo familiar (madre + hijos, padres + dependientes, etc.). Pueden existir varios titulares por grupo. El email/movil del titular sera el credencial de acceso en la app de voluntarios.'
          : 'Link volunteers that belong to the same household (mother + children, parents + dependents, etc.). Multiple holders per group are allowed. The holder\'s email/phone will be the login credential in the volunteer app.'}
      </p>

      {family.members.length === 0 ? (
        <div className="text-center text-stone-400 text-sm py-6 border border-dashed border-stone-200 rounded-sm">
          {lang === 'es'
            ? 'Voluntario sin familia asociada. Pulsa "Vincular voluntario" para empezar.'
            : 'No family linked. Click "Link volunteer" to start.'}
        </div>
      ) : (
        <div className="space-y-2">
          {family.members.map(m => {
            const isCurrent = m.volunteer_id === volunteerId;
            return (
              <div
                key={m.volunteer_id}
                className={`flex items-start gap-3 p-3 rounded-sm border ${
                  isCurrent ? 'bg-amber-50 border-amber-200' : 'bg-stone-50 border-stone-200'
                }`}
                data-testid={`family-member-${m.volunteer_id}`}
              >
                <div className="flex-shrink-0 mt-0.5">
                  {m.family_role === 'titular'
                    ? <Crown size={18} className="text-[#1f3e80]" weight="fill" />
                    : <Baby size={18} className="text-emerald-700" weight="duotone" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <a
                      href={`/volunteers/${m.volunteer_id}`}
                      className="text-sm font-semibold text-stone-900 hover:underline"
                      onClick={isCurrent ? (e) => e.preventDefault() : undefined}
                    >
                      {m.display_name}
                    </a>
                    <Badge variant="outline" className="text-[10px] font-mono">{m.volunteer_id}</Badge>
                    {isCurrent && (
                      <Badge variant="outline" className="text-[10px] bg-amber-100 border-amber-300 text-amber-800">
                        {lang === 'es' ? 'Este voluntario' : 'This volunteer'}
                      </Badge>
                    )}
                  </div>
                  <div className="text-[11px] text-stone-500 mt-0.5">
                    {m.dni && <span>DNI: {m.dni}</span>}
                    {m.dni && m.email && <span> · </span>}
                    {m.email && <span>{m.email}</span>}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    <Select
                      value={m.family_role || 'dependiente'}
                      onValueChange={(v) => handleRoleChange(m.volunteer_id, v, m.family_relationship)}
                    >
                      <SelectTrigger className="h-7 w-44 text-xs" data-testid={`family-role-${m.volunteer_id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ROLE_OPTS.map(r => (
                          <SelectItem key={r.value} value={r.value} className="text-xs">
                            {lang === 'es' ? r.es : r.en}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      defaultValue={m.family_relationship || ''}
                      placeholder={lang === 'es' ? 'Parentesco (madre, hijo, conyuge...)' : 'Relationship (mother, son, spouse...)'}
                      className="h-7 w-64 text-xs"
                      data-testid={`family-relationship-${m.volunteer_id}`}
                      onBlur={(e) => {
                        if (e.target.value !== (m.family_relationship || '')) {
                          handleRelationshipBlur(m.volunteer_id, m.family_role || 'dependiente', e.target.value);
                        }
                      }}
                    />
                  </div>
                </div>
                {!isCurrent && (
                  <button
                    type="button"
                    onClick={() => handleUnlink(m.volunteer_id)}
                    className="text-stone-400 hover:text-red-600 mt-1"
                    data-testid={`family-unlink-${m.volunteer_id}`}
                    title={lang === 'es' ? 'Desvincular' : 'Unlink'}
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            );
          })}
          {/* Allow current volunteer to leave the family */}
          <button
            type="button"
            onClick={() => handleUnlink(volunteerId)}
            className="text-[11px] text-red-600 hover:underline"
            data-testid="family-leave-btn"
          >
            {lang === 'es'
              ? `Sacar a ${currentVolunteerName || 'este voluntario'} de la familia`
              : `Remove ${currentVolunteerName || 'this volunteer'} from family`}
          </button>
        </div>
      )}

      <LinkVolunteerDialog
        open={showLink}
        onClose={() => setShowLink(false)}
        baseVolunteerId={volunteerId}
        excludeIds={family.members.map(m => m.volunteer_id)}
        onLinked={onLinked}
        lang={lang}
      />
    </div>
  );
}


function LinkVolunteerDialog({ open, onClose, baseVolunteerId, excludeIds, onLinked, lang }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [role, setRole] = useState('dependiente');
  const [relationship, setRelationship] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setQuery(''); setResults([]); setSelected(null);
      setRole('dependiente'); setRelationship('');
    }
  }, [open]);

  useEffect(() => {
    if (!open || query.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await axios.get(
          `${API_URL}/api/volunteers?all=true&search=${encodeURIComponent(query.trim())}`,
          { withCredentials: true },
        );
        const arr = Array.isArray(data) ? data : (data?.data || []);
        const filtered = arr.filter(v => v.volunteer_id !== baseVolunteerId && !excludeIds.includes(v.volunteer_id));
        setResults(filtered.slice(0, 20));
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query, open, baseVolunteerId, excludeIds]);

  const submit = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/volunteers/${baseVolunteerId}/family/link`,
        {
          linked_volunteer_id: selected.volunteer_id,
          role,
          relationship: relationship.trim(),
        },
        { withCredentials: true },
      );
      toast.success(lang === 'es' ? 'Voluntario vinculado a la familia' : 'Volunteer linked');
      onLinked(data);
    } catch (e) {
      toast.error(errText(e, lang === 'es' ? 'Error al vincular' : 'Link error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{lang === 'es' ? 'Vincular voluntario a la familia' : 'Link volunteer to family'}</DialogTitle>
          <DialogDescription>
            {lang === 'es'
              ? 'Busca el voluntario a vincular, define su rol (titular o dependiente) y el parentesco.'
              : 'Search the volunteer to link, then set their role (holder or dependent) and relationship.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={lang === 'es' ? 'Buscar por nombre, V-code, DNI o email (min 2 caracteres)' : 'Search by name, V-code, DNI or email (min 2 chars)'}
              className="pl-8 h-9 text-sm"
              data-testid="family-link-search"
              autoFocus
            />
          </div>

          {selected ? (
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-sm flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-stone-900">{selected.display_name}</div>
                <div className="text-[11px] text-stone-600">
                  {selected.volunteer_id} · DNI: {selected.personal_data?.dni || '-'} · {selected.personal_data?.email || '-'}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="text-stone-500 hover:text-red-600"
                data-testid="family-link-clear"
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <div className="border border-stone-200 rounded-sm max-h-64 overflow-y-auto">
              {query.length < 2 ? (
                <p className="text-xs text-stone-400 text-center py-4">
                  {lang === 'es' ? 'Escribe al menos 2 caracteres' : 'Type at least 2 characters'}
                </p>
              ) : searching ? (
                <p className="text-xs text-stone-400 text-center py-4">
                  {lang === 'es' ? 'Buscando...' : 'Searching...'}
                </p>
              ) : results.length === 0 ? (
                <p className="text-xs text-stone-400 text-center py-4">
                  {lang === 'es' ? 'Sin resultados' : 'No results'}
                </p>
              ) : (
                results.map(v => (
                  <button
                    key={v.volunteer_id}
                    type="button"
                    onClick={() => setSelected(v)}
                    className="w-full text-left px-3 py-2 hover:bg-stone-50 border-b border-stone-100 last:border-0"
                    data-testid={`family-link-result-${v.volunteer_id}`}
                  >
                    <div className="text-sm text-stone-900">{v.display_name}</div>
                    <div className="text-[11px] text-stone-500">
                      {v.volunteer_id} · {v.personal_data?.dni || '-'} · {v.personal_data?.email || '-'}
                    </div>
                  </button>
                ))
              )}
            </div>
          )}

          {selected && (
            <>
              <div className="space-y-1.5">
                <Label className="text-xs">{lang === 'es' ? 'Rol' : 'Role'}</Label>
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger className="h-9" data-testid="family-link-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLE_OPTS.map(r => (
                      <SelectItem key={r.value} value={r.value}>
                        {lang === 'es' ? r.es : r.en}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{lang === 'es' ? 'Parentesco (libre)' : 'Relationship (free text)'}</Label>
                <Input
                  value={relationship}
                  onChange={(e) => setRelationship(e.target.value)}
                  placeholder={lang === 'es' ? 'madre, hijo, tutor legal, conyuge...' : 'mother, son, legal guardian, spouse...'}
                  className="h-9"
                  data-testid="family-link-relationship"
                />
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{lang === 'es' ? 'Cancelar' : 'Cancel'}</Button>
          <Button
            onClick={submit}
            disabled={!selected || saving}
            data-testid="family-link-confirm"
          >
            <LinkIcon size={14} className="mr-1.5" />
            {saving ? '...' : (lang === 'es' ? 'Vincular' : 'Link')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
